import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Parse Pipe Self-Describing File Step
 *
 * Generic parser for GAINS-style pipe-self-describing files. Each record is
 * one line that begins with a record-type tag (e.g. `VENDOR_MASTER`,
 * `REXEL_NET_AVAILABLE`) followed by alternating `FIELD_NAME|VALUE` pairs.
 *
 * Configure via `recordType` (which records to keep) and `columnNames` (the
 * snake_case allow-list of pipe keys to extract). Keys outside the list are
 * silently dropped — this keeps the staging row clean and prevents NULL-FK
 * violations when the source emits extra columns.
 *
 * Used by VENDMAST, GSNETDEF, and (in future) other `GS*-prefix` files.
 *
 * Example workflow:
 * ```
 * Blob Storage Fetch → Parse Pipe Self-Describing File → Master Data Bulk Insert
 * ```
 */
export const parsePipeSelfDescribingFileStep = defineStepConfig({
  type: "parse_pipe_self_describing_file",
  name: "Parse Pipe Self-Describing File",
  description:
    "Parse a GAINS-style pipe-self-describing file (KEY|VALUE pairs per record) into structured rows",
  category: "Transformation",
  libraryModule: "Procurement",

  inputSchema: z.object({
    fileBlobKey: z.string().describe("Blob key of the downloaded file"),
    containerName: z.string().describe("Azure container name"),
    storageProfile: z
      .string()
      .optional()
      .describe("Storage profile identifier"),
    fileName: z.string().describe("Original file name"),
  }),

  outputSchema: z.object({
    rowsBlobKey: z
      .string()
      .describe("Blob key of the NDJSON scratch file with parsed rows"),
    containerName: z.string().describe("Pass-through Azure container name"),
    storageProfile: z
      .string()
      .optional()
      .describe("Pass-through storage profile identifier"),
    fileName: z.string().describe("Pass-through original file name"),
    rowCount: z.number().describe("Number of rows parsed"),
  }),

  configSchema: z.object({
    recordType: z
      .string()
      .describe(
        "The record-type tag at the start of each pipe line (e.g. " +
          "VENDOR_MASTER, REXEL_NET_AVAILABLE). Lines whose first token does " +
          "not match this exactly are skipped.",
      ),
    trimWhitespace: z
      .boolean()
      .prefault(true)
      .describe("Trim whitespace from parsed values"),
    skipEmptyRows: z
      .boolean()
      .prefault(true)
      .describe("Skip rows where all values are empty"),
    columnNames: z
      .array(z.string())
      .describe(
        "Allow-list of expected snake_case column names. Pipe-delimited keys " +
          "not in this list are dropped silently. Required — there is no safe " +
          "default that works across file types.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "10 seconds",
    },
  },
});
